import React, { useState, useEffect, useRef } from 'react';
import { Button, message, Spin, Avatar, Upload } from 'antd';
import { Building2, Save, Upload as UploadIcon } from 'lucide-react';
import Inputs from '../../components/inputs';
import axios from 'axios';
import { API_URL, BUCKET_URL } from '../../config/config';
import { getHeaderOptions } from '../../services/getHeaderOptions';
import { useFileUpload } from '../../hooks/useFileUpload';
import './settings.scss';

interface OrganizationData {
  name: string;
  phone: string;
  currency: string;
  timeZone: string;
  website: string;
  address: string;
  description: string;
  socialLinks: {
    linkedin: string;
    twitter: string;
    facebook: string;
    instagram: string;
  };
  iconUrl: string;
  profileUrl: string;
  faviconUrl: string;
  isAutomationAutoTaskAssignEnable: boolean;
  isIntegrationDuplicateLeadAllow: boolean;
  supportContact: {
    phone: string;
    whatsapp: string;
    email: string;
  };
  taskNotificationOverdueDays: number;
  lastLeadSequenceId: string;
  isCallRecordingEnabled: boolean;
  address1: string;
  address2: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  gst: string;
  businessCategory?: string;
}

const OrganizationSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [organizationId, setOrganizationId] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [orgData, setOrgData] = useState<OrganizationData>({
    name: '',
    phone: '',
    currency: 'USD',
    timeZone: '',
    website: '',
    address: '',
    description: '',
    socialLinks: {
      linkedin: '',
      twitter: '',
      facebook: '',
      instagram: ''
    },
    iconUrl: '',
    profileUrl: '',
    faviconUrl: '',
    isAutomationAutoTaskAssignEnable: false,
    isIntegrationDuplicateLeadAllow: false,
    supportContact: {
      phone: '',
      whatsapp: '',
      email: ''
    },
    taskNotificationOverdueDays: 0,
    lastLeadSequenceId: '',
    isCallRecordingEnabled: false,
    address1: '',
    address2: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    gst: '',
    businessCategory: ''
  });

  useEffect(() => {
    fetchOrganizationData();
  }, []);

  const fetchOrganizationData = async () => {
    setLoading(true);
    try {
      const headers = {
        ...getHeaderOptions(),
        Authorization: localStorage.getItem('auth_token') || ''
      };
      const response = await axios.get(`${API_URL}api/v1/organization`, { headers });
      if (response.data && response.data.data && response.data.data.length > 0) {
        const org = response.data.data[0];
        setOrganizationId(org._id);
        setOrgData({
          name: org.name || '',
          phone: org.phone || '',
          currency: org.currency || 'USD',
          timeZone: org.timeZone || '',
          website: org.website || '',
          address: org.address || '',
          description: org.description || '',
          socialLinks: org.socialLinks || {
            linkedin: '',
            twitter: '',
            facebook: '',
            instagram: ''
          },
          iconUrl: org.iconUrl || '',
          profileUrl: org.profileUrl || '',
          faviconUrl: org.faviconUrl || '',
          isAutomationAutoTaskAssignEnable: org.isAutomationAutoTaskAssignEnable || false,
          isIntegrationDuplicateLeadAllow: org.isIntegrationDupCheckEnabled || false,
          supportContact: org.supportContact || {
            phone: '',
            whatsapp: '',
            email: ''
          },
          taskNotificationOverdueDays: org.taskNotificationOverdueDays || 0,
          lastLeadSequenceId: org.lastLeadSequenceId || '',
          isCallRecordingEnabled: org.isCallRecordingEnabled || false,
          address1: org.address1 || '',
          address2: org.address2 || '',
          city: org.city || '',
          state: org.state || '',
          country: org.country || '',
          zipCode: org.zipCode || '',
          gst: org.gst || '',
          businessCategory: org.businessCategory || ''
        });
      }
    } catch (error) {
      console.error('Error fetching organization data:', error);
      message.error('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setOrgData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof OrganizationData] as any),
          [child]: value
        }
      }));
    } else {
      setOrgData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // File upload hook
  const { uploadFile, uploading: uploadingIcon, progress } = useFileUpload({
    recordId: organizationId,
    type: 'organization_icon_url',
    maxSize: 5,
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
    onSuccess: (files) => {
      if (files && files.length > 0) {
        setOrgData(prev => ({
          ...prev,
          iconUrl: files[0].filePath
        }));
        message.success('Organization icon uploaded successfully');
      }
    },
    onError: (error) => {
      message.error(`Upload failed: ${error}`);
    }
  });

  const handleIconUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!organizationId) {
      message.error('Organization ID not found. Please refresh the page.');
      return;
    }

    await uploadFile(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = {
        ...getHeaderOptions(),
        Authorization: localStorage.getItem('auth_token') || ''
      };
      await axios.put(`${API_URL}api/v2/organization`, orgData, { headers });
      message.success('Organization settings saved successfully');
    } catch (error) {
      console.error('Error saving organization data:', error);
      message.error('Failed to save organization settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="organization-settings">
      <div className="settings-section-header">
        <Building2 size={24} />
        <div>
          <h2>Organization Settings</h2>
          <p>Manage your organization's information and preferences</p>
        </div>
      </div>

      <div className="settings-form">
        {/* Icon Upload Section */}
        <div className="settings-section">
          <h3>Organization Icon</h3>
          <div className="icon-upload-section">
            <Avatar
              size={100}
              src={orgData.iconUrl ? `${BUCKET_URL}${orgData.iconUrl}` : undefined}
              icon={<Building2 />}
              style={{ marginBottom: '1rem' }}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleIconUpload}
              style={{ display: 'none' }}
            />
            <Button
              icon={<UploadIcon size={16} />}
              onClick={() => fileInputRef.current?.click()}
              loading={uploadingIcon}
            >
              {uploadingIcon ? `Uploading... ${progress}%` : 'Upload Icon'}
            </Button>
            <p className="text-muted">Recommended: 512x512px, Max 5MB (JPG, PNG, WEBP)</p>
          </div>
        </div>

        {/* Basic Information */}
        <div className="settings-section">
          <h3>Basic Information</h3>
          <div className="settings-grid">
            <Inputs
              label="Organization Name"
              name="name"
              type="text"
              value={orgData.name}
              onChange={handleInputChange}
              placeholder="Enter organization name"
              className="form-control"
              autoComplete="off"
              isRequired={true}
              options={[]}
              id="org-name"
            />
            <Inputs
              label="Phone"
              name="phone"
              type="phone"
              value={orgData.phone}
              onChange={handleInputChange}
              placeholder="Enter phone number"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-phone"
            />
            <Inputs
              label="Currency"
              name="currency"
              type="text"
              value={orgData.currency}
              onChange={handleInputChange}
              placeholder="Currency"
              className="form-control"
              autoComplete="off"
              isRequired={true}
              disabled={true}
              options={[]}
              id="org-currency"
            />
            <Inputs
              label="Time Zone"
              name="timeZone"
              type="text"
              value={orgData.timeZone}
              onChange={handleInputChange}
              placeholder="Time zone"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              disabled={true}
              options={[]}
              id="org-timezone"
            />
            <Inputs
              label="Website"
              name="website"
              type="url"
              value={orgData.website}
              onChange={handleInputChange}
              placeholder="https://example.com"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-website"
            />
            <Inputs
              label="GST Number"
              name="gst"
              type="text"
              value={orgData.gst}
              onChange={handleInputChange}
              placeholder="Enter GST number"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-gst"
            />
          </div>
        </div>

        {/* Address Information */}
        <div className="settings-section">
          <h3>Address Information</h3>
          <div className="settings-grid">
            <Inputs
              label="Address Line 1"
              name="address1"
              type="text"
              value={orgData.address1}
              onChange={handleInputChange}
              placeholder="Enter address line 1"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-address1"
            />
            <Inputs
              label="Address Line 2"
              name="address2"
              type="text"
              value={orgData.address2}
              onChange={handleInputChange}
              placeholder="Enter address line 2"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-address2"
            />
            <Inputs
              label="City"
              name="city"
              type="text"
              value={orgData.city}
              onChange={handleInputChange}
              placeholder="Enter city"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-city"
            />
            <Inputs
              label="State"
              name="state"
              type="text"
              value={orgData.state}
              onChange={handleInputChange}
              placeholder="Enter state"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-state"
            />
            <Inputs
              label="Country"
              name="country"
              type="text"
              value={orgData.country}
              onChange={handleInputChange}
              placeholder="Enter country"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-country"
            />
            <Inputs
              label="Zip Code"
              name="zipCode"
              type="text"
              value={orgData.zipCode}
              onChange={handleInputChange}
              placeholder="Enter zip code"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-zipcode"
            />
          </div>
        </div>

        {/* Social Links */}
        <div className="settings-section">
          <h3>Social Links</h3>
          <div className="settings-grid">
            <Inputs
              label="LinkedIn"
              name="socialLinks.linkedin"
              type="url"
              value={orgData.socialLinks.linkedin}
              onChange={handleInputChange}
              placeholder="https://linkedin.com/company/..."
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-linkedin"
            />
            <Inputs
              label="Twitter"
              name="socialLinks.twitter"
              type="url"
              value={orgData.socialLinks.twitter}
              onChange={handleInputChange}
              placeholder="https://twitter.com/..."
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-twitter"
            />
            <Inputs
              label="Facebook"
              name="socialLinks.facebook"
              type="url"
              value={orgData.socialLinks.facebook}
              onChange={handleInputChange}
              placeholder="https://facebook.com/..."
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-facebook"
            />
            <Inputs
              label="Instagram"
              name="socialLinks.instagram"
              type="url"
              value={orgData.socialLinks.instagram}
              onChange={handleInputChange}
              placeholder="https://instagram.com/..."
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="org-instagram"
            />
          </div>
        </div>

        {/* Support Contact */}
        <div className="settings-section">
          <h3>Support Contact</h3>
          <div className="settings-grid">
            <Inputs
              label="Support WhatsApp"
              name="supportContact.whatsapp"
              type="phone"
              value={orgData.supportContact.whatsapp}
              onChange={handleInputChange}
              placeholder="Enter WhatsApp number"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="support-whatsapp"
            />
            <Inputs
              label="Support Email"
              name="supportContact.email"
              type="email"
              value={orgData.supportContact.email}
              onChange={handleInputChange}
              placeholder="support@example.com"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="support-email"
            />
          </div>
        </div>

        {/* Description */}
        <div className="settings-section">
          <h3>Description</h3>
          <Inputs
            label="Description"
            name="description"
            type="text"
            value={orgData.description}
            onChange={handleInputChange}
            placeholder="Enter organization description"
            className="form-control"
            autoComplete="off"
            isRequired={false}
            options={[]}
            id="org-description"
          />
        </div>

        {/* Save Button */}
        <div className="settings-actions">
          <Button
            type="primary"
            icon={<Save size={16} />}
            onClick={handleSave}
            loading={saving}
            size="large"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSettings;
